import {
  json,
  redirect,
  type LoaderFunctionArgs,
  type MetaFunction,
} from '@remix-run/node'
import { useLoaderData, useRouteLoaderData } from '@remix-run/react'
import { useTranslation } from 'react-i18next'

import { GeneralErrorBoundary, NotFoundError } from '#app/components/errors.tsx'
import { Layout } from '#app/components/layout.tsx'
import { RichText } from '#app/components/rich-text.tsx'
import { Button } from '#app/components/ui/button.tsx'
import { type RootLoaderType } from '#app/root.tsx'
import type { Handle } from '#app/types/index.ts'
import { defaultLocale, getLocaleFromRequest } from '#app/utils/i18n.ts'
import { mapImage } from '#app/utils/mappers.ts'
import { cn, getUrl } from '#app/utils/misc.tsx'
import { getSocialMetas } from '#app/utils/seo.ts'
import { getAllPageSlugs, getPageBySlug } from '#app/utils/strapi.ts'

export const handle: Handle = {
  getSitemapEntries: async request => {
    const locale = getLocaleFromRequest(request)
    const pages = await getAllPageSlugs(locale)
    return pages.map(page => ({
      route: `${locale === defaultLocale ? '' : `/${locale}`}${page.attributes.slug === 'home' ? '' : `/${page.attributes.slug}`}`,
      priority: page.attributes.slug === 'home' ? 0.8 : 0.4,
    }))
  },
}

export async function loader({ request, params }: LoaderFunctionArgs) {
  const locale = getLocaleFromRequest(request)
  const { pathname } = new URL(request.url)

  const slug =
    locale === defaultLocale && params.locale !== undefined
      ? `${params.locale}${params['*'] ? `/${params['*']}` : ''}`
      : params['*']

  const page = await getPageBySlug(locale, slug ?? 'home')

  if (!page) {
    throw new Response('Not found', { status: 404 })
  }

  // Home page has slug "home" but we don't want that url to work
  if (pathname.includes('home')) {
    throw redirect(locale === defaultLocale ? '/' : `/${locale}`)
  }

  return json({ page })
}

export const meta: MetaFunction<typeof loader, { root: RootLoaderType }> = ({
  data,
  matches,
}) => {
  const rootData = matches.find(m => m.id === 'root')?.data

  if (data?.page?.SEO) {
    const meta = data.page.SEO
    return getSocialMetas({
      title: meta?.title,
      description: meta?.description,
      image: meta?.image.data?.attributes.url,
      url: getUrl(rootData.requestInfo),
    })
  } else {
    return [
      { title: 'Not Found' },
      { name: 'description', content: 'Not found' },
    ]
  }
}

export default function SplatRoute() {
  const rootData = useRouteLoaderData<RootLoaderType>('root')
  const data = useLoaderData<typeof loader>()
  const { i18n, t } = useTranslation()

  const image = mapImage(data.page.image.data?.attributes)

  if (data.page.name === 'home') {
    return (
      <main className="flex">
        <section className="flex min-h-screen w-full flex-col justify-between overflow-auto p-6 lg:w-1/2 lg:pr-10">
          <div className="flex flex-col flex-wrap justify-between lg:flex-row">
            <h1 className="mb-6 flex w-[30%] grow flex-col items-baseline justify-between text-[62px] font-semibold uppercase leading-none text-primary lg:mb-0 lg:text-[72px]">
              <div>Cafe</div>
              <div>Resto</div>
              <div>Bar</div>
            </h1>
            <div className="w-full lg:w-[70%] lg:pl-8">
              <img src="/images/logo.svg" alt="Logo of V12" />
            </div>
          </div>
          <div className="mx-auto my-24 w-full max-w-[400px] lg:my-0">
            <Button asChild size="lg" className="w-full">
              <a href="#tebi-reservations">{t('cta.reserve')}</a>
            </Button>
          </div>
          <div>
            <RichText content={data.page.description} justifyText />
            <ul className="flex flex-col flex-wrap gap-6 pb-14 lg:flex-row lg:justify-between lg:pb-0 lg:pt-8">
              {rootData.navigation.map(item => (
                <li
                  key={item.id}
                  className="whitespace-nowrap font-semibold uppercase"
                >
                  {item.url ? (
                    <a
                      className={cn(
                        'text-black hover:underline ',
                        item.url && 'text-primary',
                      )}
                      href={
                        i18n.language === 'nl' || item.url.includes('http')
                          ? item.url
                          : `/${i18n.language}${item.url}`
                      }
                      target={item.openInNewTab ? '_blank' : undefined}
                      rel={
                        item.openInNewTab ? 'noopener noreferrer' : undefined
                      }
                    >
                      {item.text}
                    </a>
                  ) : (
                    item.text
                  )}
                </li>
              ))}
            </ul>
          </div>
        </section>
        <aside className="hidden h-screen w-1/2 overflow-hidden lg:block">
          <img
            className="h-full w-full object-cover"
            src={image.url}
            alt={image.alt}
          />
        </aside>
      </main>
    )
  }

  return (
    <Layout imageSrc={image.url} imageAlt={image.alt}>
      <h1 className="mb-8 block text-h2 text-primary">{data.page.title}</h1>
      {data.page.description ? (
        <RichText content={data.page.description} />
      ) : null}
    </Layout>
  )
}

export function ErrorBoundary() {
  return <GeneralErrorBoundary statusHandlers={{ 404: NotFoundError }} />
}
